import React, { useMemo, useEffect, useCallback, useRef } from 'react';
import TableContainerReactTable from '../../../Components/Common/TableContainerReactTable';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { useLazyGetProductDetailsQuery } from '../redux/productApiSlice';
import { setSelectedProduct, setSelectedProductLoading, setSelectedRowId, clearSelectedProduct } from '../redux/productSlice';
import LoadingIndicator from '../../../Components/Loading/LoadingIndicator';
import { toast } from 'react-toastify';
import {
  useVirtualizer,
  VirtualItem,
  Virtualizer,
} from '@tanstack/react-virtual'

// Memoized selector using createSelector for better performance
const selectProducts = createSelector(
  [(state: RootState) => state.products?.products],
  (products) => products ?? []
);

const ProductTable = () => {
  const dispatch = useDispatch();
  
  // Get loading state and selectedRowId from Redux
  const isLoading = useSelector((state: RootState) => state.products.loading);
  const error = useSelector((state: RootState) => state.products.error);
  const selectedRowId = useSelector((state: RootState) => state.products.selectedRowId);

  const [getProductDetails] = useLazyGetProductDetailsQuery();

  // The virtualizer will need a reference to the scrollable container element
  const tableContainerRef = React.useRef<HTMLDivElement>(null)

  // using Memoized selector for selecting products
  const products = useSelector(selectProducts);
  
  // Ref to track if we're currently fetching details to prevent race conditions
  const isFetchingDetailsRef = useRef(false);
  
  // Ref to track the last products array to detect actual changes
  const lastProductsRef = useRef<any[]>([]);

  // Using pagination only (virtual mode removed)

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "productName",
        enableColumnFilter: false,
        size:100,
        cell: (cell: any) => {
          return (
            <div className="text-left">
              {cell.getValue()}
            </div>
          );
        },
      },
      {
        header: "Product Code",
        accessorKey: "productCode",
        enableColumnFilter: false,
      },
      {
        header: "Category",
        accessorKey: "category",
        enableColumnFilter: false,
      },
      {
        header: "Vendor",
        accessorKey: "vendorName",
        enableColumnFilter: false,
      },
      {
        header: "Active",
        accessorKey: "activeFlag",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const isActive = cell.getValue();
          return isActive ? (
            <div className="text-start ps-1">
              <i className="ri-checkbox-circle-line fs-17 align-middle"></i>
            </div>
          ) : null;
        },
      },
      {
        header: "dynamicValue",
        accessorKey: "dynamicVal",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-right">
              {value}
            </div>
          );
        },
      },
      {
        header: "dynamicValueSecond",
        accessorKey: "dynamicValSecond",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-right">
              {value}
            </div>
          );
        },
      }
    ],
    []
  );


  // Create selectedRows object based on Redux selectedRowId
  const selectedRows = useMemo(() => {
    if (!selectedRowId) return {};
    return { [selectedRowId]: true };
  }, [selectedRowId]);

  // Extracted function to select a product and fetch its details
  const selectProductAndFetchDetails = useCallback(async (product: any, showSuccessToast = true) => {
    if (!product?.rowId || isFetchingDetailsRef.current) {
      return;
    }

    const rowId = product.rowId?.toString();
    
    // Prevent race conditions
    isFetchingDetailsRef.current = true;
    
    try {
      // Update Redux state with selected row ID
      dispatch(setSelectedRowId(rowId));
      
      // Set loading state
      dispatch(setSelectedProductLoading(true));
      
      const productDetailsResponse = await getProductDetails({
        productRowId: product.rowId
      });

      if (productDetailsResponse?.data?.data?.[0]) {
        // Dispatch to existing product slice
        dispatch(setSelectedProduct(productDetailsResponse.data.data[0]));
        if (showSuccessToast) {
          toast.success('Product details fetched successfully');
        }
      } else {
        console.warn('No product details found in response');
        dispatch(setSelectedProductLoading(false));
        if (showSuccessToast) {
          toast.error('No product details found in response');
        }
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      dispatch(setSelectedProductLoading(false));
      if (showSuccessToast) {
        toast.error('Error fetching product details. Please try again.');
      }
    } finally {
      isFetchingDetailsRef.current = false;
    }
  }, [dispatch, getProductDetails]);

  // Handle row selection change
  const handleRowSelectionChange = (updaterOrValue: any) => {
    const newSelection = typeof updaterOrValue === 'function' 
      ? updaterOrValue(selectedRows) 
      : updaterOrValue;
    
    // Get the first selected row ID (since we only allow single selection)
    const selectedRowIds = Object.keys(newSelection).filter(key => newSelection[key]);
    const newSelectedRowId = selectedRowIds.length > 0 ? selectedRowIds[0] : null;
    
    // Update Redux state
    dispatch(setSelectedRowId(newSelectedRowId));
  };

  // Handle row click for single selection
  const handleRowClick = async (row: any) => {
    // Use the extracted function with success toast
    await selectProductAndFetchDetails(row.original, true);
  };

  // Auto-select first product when products array changes
  useEffect(() => {
    const hasProducts = products && products.length > 0;
    
    // More robust change detection: check both length and first item ID
    const lastProducts = lastProductsRef.current;
    const productsChanged = 
      products.length !== lastProducts.length ||
      (hasProducts && (!lastProducts[0] || products[0]?.rowId !== lastProducts[0]?.rowId)) ||
      (!hasProducts && lastProducts.length > 0);
    
    // Update the ref with current products array
    lastProductsRef.current = [...products];
    
    if (hasProducts && productsChanged && !isLoading) {
      // Check if the currently selected product still exists in the new results
      const currentlySelectedExists = selectedRowId && 
        products.some(p => p.rowId?.toString() === selectedRowId);
      
      // Only auto-select first product if:
      // 1. No product is currently selected, OR
      // 2. Currently selected product no longer exists in results (e.g., after deletion)
      if (!selectedRowId || !currentlySelectedExists) {
        const firstProduct = products[0];
        if (firstProduct) {
          selectProductAndFetchDetails(firstProduct, false);
        }
      }
    } else if (!hasProducts && productsChanged) {
      // Clear selection when no products are available
      dispatch(clearSelectedProduct());
      dispatch(setSelectedRowId(null));
      // Reset the fetching flag
      isFetchingDetailsRef.current = false;
    }
  }, [products, isLoading, selectProductAndFetchDetails, dispatch, selectedRowId]);

  // Additional effect to handle cleanup when component unmounts
  useEffect(() => {
    return () => {
      // Reset the fetching flag on unmount to prevent memory leaks
      isFetchingDetailsRef.current = false;
    };
  }, []);

  // Show loading indicator when fetching products
  if (isLoading) {
    return (
      <LoadingIndicator
        isLoading={true}
        title="Searching products..."
        height="400px"
        className="text-center"
      />
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <LoadingIndicator
        error={error}
        height="200px"
        className="text-center"
      />
    );
  }

  return (
    <React.Fragment>
      <TableContainerReactTable
        columns={columns}
        data={products}
        isGlobalFilter={false}
        customPageSize={10}
        tableClass="table-centered align-middle table-nowrap table-sm mb-0"
        theadClass="table-light sticky-top"
        divClass="table-responsive"
        enableRowSelection={true}
        enableMultiRowSelection={false}
        rowSelection={selectedRows}
        onRowSelectionChange={handleRowSelectionChange}
        getRowId={(row: any) => row.id}
        onRowClick={handleRowClick}
        verticalLines={true}
        enableVirtualScroll={true}
        tableBodyHeight="60vh"
        allowModeToggle={true}
      />
    </React.Fragment>
  );
};

export default ProductTable; 